/* i18next, configured once for the cockpit.
 *
 * Importing this module initialises i18next as a side effect, the same way
 * `initializeScale()` prepares the design system. `main.tsx` imports it before
 * the first render so no component ever mounts against an uninitialised
 * instance.
 *
 * Both catalogues are bundled rather than fetched. There are two languages and
 * an operator switching between them is doing so mid-shift; a network round
 * trip to change the sidebar would be a worse trade than the bytes. It also
 * means `init` completes synchronously, so nothing suspends on first paint.
 *
 * The `languageChanged` subscription is why nothing else has to remember to
 * persist a choice. A switcher calls `changeLanguage` and stops there;
 * remembering the preference and setting `<html lang>` follow from the event.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import {
  applyDocumentLanguage,
  FALLBACK_LOCALE,
  initialLocale,
  isLocale,
  rememberLocale,
} from './locale';
import { defaultNS, namespaces, resources } from './resources';

const startingLocale = initialLocale();

void i18n.use(initReactI18next).init({
  resources,
  lng: startingLocale,
  fallbackLng: FALLBACK_LOCALE,
  defaultNS,
  ns: namespaces,
  interpolation: {
    // React escapes everything it renders. Escaping again here turns an
    // ampersand in a customer name into `&amp;` on screen.
    escapeValue: false,
  },
});

applyDocumentLanguage(startingLocale);

i18n.on('languageChanged', (next: string) => {
  // i18next will happily report a language nobody configured — a fallback
  // resolution, or a stray `changeLanguage('fr')`. Only the two the cockpit
  // has catalogues for are worth remembering.
  if (!isLocale(next)) return;
  rememberLocale(next);
  applyDocumentLanguage(next);
});

export { i18n };
